import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
} from '@mui/material';
import {
  Assignment,
  ReportProblem,
  Schedule,
  RuleFolder,
  Flag,
  FolderOpen,
} from '@mui/icons-material';
import projectAnalyticsService from '../../services/projectAnalyticsService';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  ErrorState,
  TableEmptyState,
  StatusTone,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';

// ---- pure formatters ----
const isNum = (v: any) => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtDate = (v: any) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const daysOverdue = (v: any) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  return diff > 0 ? diff : 0;
};

const TASK_STATUS_TONE: Record<string, StatusTone> = {
  TODO: 'neutral',
  IN_PROGRESS: 'info',
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};
const TASK_STATUS_LABEL: Record<string, string> = {
  TODO: 'To do',
  IN_PROGRESS: 'In progress',
  ON_HOLD: 'On hold',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const PRIORITY_TINT: Record<string, string> = {
  LOW: '#64748b',
  MEDIUM: '#2a78d6',
  HIGH: '#eda100',
  CRITICAL: '#d03b3b',
};

const CR_STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  UNDER_REVIEW: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
  IMPLEMENTED: 'success',
};

const MILESTONE_STATUS_TONE: Record<string, StatusTone> = {
  PENDING: 'neutral',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  AT_RISK: 'warning',
  DELAYED: 'error',
};

const PROJECT_STATUS_TONE: Record<string, StatusTone> = {
  PLANNING: 'info',
  IN_PROGRESS: 'info',
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

interface SectionProps {
  id: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ id, title, subtitle, children }) => (
  <Box id={id} sx={{ mb: 3 }}>
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const ProjectMyDayPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    projectAnalyticsService
      .myDay()
      .then((res) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load your project day.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const todayTasks = data?.todayTasks || [];
  const overdueTasks = data?.overdueTasks || [];
  const timesheets = data?.pendingApprovals?.timesheets || [];
  const changeRequests = data?.pendingApprovals?.changeRequests || [];
  const milestonesDue = data?.milestonesDue || [];
  const myProjects = data?.myProjects || [];

  const goProject = (projectId: any) => {
    if (projectId != null) navigate(`/projects/${projectId}/workspace`);
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stats = useMemo(
    () => [
      { key: 'today', title: "Today's Tasks", value: todayTasks.length, icon: <Assignment />, color: '#2a78d6' },
      { key: 'overdue', title: 'Overdue Tasks', value: overdueTasks.length, icon: <ReportProblem />, color: '#d03b3b' },
      { key: 'timesheets', title: 'Timesheets to Approve', value: timesheets.length, icon: <Schedule />, color: '#eda100' },
      { key: 'crs', title: 'Change Requests', value: changeRequests.length, icon: <RuleFolder />, color: '#4a3aa7' },
      { key: 'milestones', title: 'Milestones Due (7d)', value: milestonesDue.length, icon: <Flag />, color: '#1baf7a' },
      { key: 'projects', title: 'My Active Projects', value: myProjects.length, icon: <FolderOpen />, color: '#008300' },
    ],
    [todayTasks, overdueTasks, timesheets, changeRequests, milestonesDue, myProjects]
  );

  return (
    <Box>
      <PageHeader title="My Project Day" subtitle="Today's tasks, approvals, and milestones — scoped to you" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {stats.map((s) => (
              <Grid key={s.key} size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard title={s.title} value={s.value} icon={s.icon} color={s.color} onClick={() => scrollTo(s.key)} />
              </Grid>
            ))}
          </Grid>

          <Section id="today" title="Today's Tasks" subtitle="Assigned to you, due today">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Task</TableCell>
                    <TableCell sx={headerCellSx}>Project</TableCell>
                    <TableCell sx={headerCellSx}>Priority</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Due Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todayTasks.map((t: any) => (
                    <TableRow key={t.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => goProject(t.project_id)}>
                      <TableCell>{t.title || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{t.project_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.project_code || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TypePill label={t.priority || '—'} tint={PRIORITY_TINT[t.priority] || '#64748b'} />
                      </TableCell>
                      <TableCell>
                        <StatusDot label={TASK_STATUS_LABEL[t.status] || t.status || '—'} tone={TASK_STATUS_TONE[t.status] || 'neutral'} />
                      </TableCell>
                      <TableCell>{fmtDate(t.due_date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && !todayTasks.length && <TableEmptyState title="Nothing due today" hint="You're all caught up." />}
            </TableSurface>
          </Section>

          <Section id="overdue" title="Overdue Tasks" subtitle="Past their due date and still open">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Task</TableCell>
                    <TableCell sx={headerCellSx}>Project</TableCell>
                    <TableCell sx={headerCellSx}>Priority</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Due Date</TableCell>
                    <TableCell sx={headerCellSx}>Overdue By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overdueTasks.map((t: any) => (
                    <TableRow key={t.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => goProject(t.project_id)}>
                      <TableCell>{t.title || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{t.project_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.project_code || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TypePill label={t.priority || '—'} tint={PRIORITY_TINT[t.priority] || '#64748b'} />
                      </TableCell>
                      <TableCell>
                        <StatusDot label={TASK_STATUS_LABEL[t.status] || t.status || '—'} tone={TASK_STATUS_TONE[t.status] || 'neutral'} />
                      </TableCell>
                      <TableCell>{fmtDate(t.due_date)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                          {daysOverdue(t.due_date) ?? '—'}d
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && !overdueTasks.length && <TableEmptyState title="No overdue tasks" hint="Nothing slipping right now." />}
            </TableSurface>
          </Section>

          <Section id="milestones" title="Milestones Due" subtitle="In the next 7 days, owned by or reporting to you">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Milestone</TableCell>
                    <TableCell sx={headerCellSx}>Project</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Target Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {milestonesDue.map((m: any) => (
                    <TableRow key={m.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => goProject(m.project_id)}>
                      <TableCell>{m.name || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{m.project_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {m.project_code || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusDot label={m.status || '—'} tone={MILESTONE_STATUS_TONE[m.status] || 'neutral'} />
                      </TableCell>
                      <TableCell>{fmtDate(m.target_date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && !milestonesDue.length && <TableEmptyState title="No milestones due" hint="Nothing on the horizon this week." />}
            </TableSurface>
          </Section>

          <Section id="timesheets" title="Pending Timesheet Approvals" subtitle="Submitted by your project teams, awaiting review">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>User</TableCell>
                    <TableCell sx={headerCellSx}>Project</TableCell>
                    <TableCell sx={headerCellSx}>Date</TableCell>
                    <TableCell sx={headerCellSx} align="right">Hours</TableCell>
                    <TableCell sx={headerCellSx} align="right">Billing Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timesheets.map((ts: any) => (
                    <TableRow key={ts.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => goProject(ts.project_id)}>
                      <TableCell>{ts.user_name || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{ts.project_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ts.project_code || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{fmtDate(ts.entry_date)}</TableCell>
                      <TableCell align="right">{isNum(ts.hours) ? Number(ts.hours).toFixed(2) : '—'}</TableCell>
                      <TableCell align="right">{ts.billing_rate != null ? fmtMoney(ts.billing_rate) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && !timesheets.length && <TableEmptyState title="No timesheets pending" hint="Nothing waiting on your approval." />}
            </TableSurface>
          </Section>

          <Section id="crs" title="Pending Change Requests" subtitle="Submitted or under review on projects you manage">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Change Request</TableCell>
                    <TableCell sx={headerCellSx}>Project</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx} align="right">Cost Impact</TableCell>
                    <TableCell sx={headerCellSx} align="right">Schedule Impact</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changeRequests.map((cr: any) => (
                    <TableRow key={cr.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => goProject(cr.project_id)}>
                      <TableCell>{cr.title || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{cr.project_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cr.project_code || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusDot label={cr.status || '—'} tone={CR_STATUS_TONE[cr.status] || 'neutral'} />
                      </TableCell>
                      <TableCell align="right">{cr.cost_impact != null ? fmtMoney(cr.cost_impact) : '—'}</TableCell>
                      <TableCell align="right">{cr.schedule_impact_days != null ? `${cr.schedule_impact_days}d` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && !changeRequests.length && <TableEmptyState title="No change requests pending" hint="Nothing waiting on your review." />}
            </TableSurface>
          </Section>

          <Section id="projects" title="My Projects" subtitle="Active projects you manage">
            <TableSurface>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Code</TableCell>
                    <TableCell sx={headerCellSx}>Project</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myProjects.map((p: any) => (
                    <TableRow key={p.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => goProject(p.id)}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.project_code || '—'}</TableCell>
                      <TableCell>{p.project_name || '—'}</TableCell>
                      <TableCell>
                        <StatusDot label={p.status || '—'} tone={PROJECT_STATUS_TONE[p.status] || 'neutral'} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(Math.max(Number(p.progress_percent) || 0, 0), 100)}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 32 }}>
                            {isNum(p.progress_percent) ? `${Math.round(Number(p.progress_percent))}%` : '—'}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && !myProjects.length && <TableEmptyState title="No active projects" hint="You aren't managing any active projects right now." />}
            </TableSurface>
          </Section>
        </>
      )}
    </Box>
  );
};

export default ProjectMyDayPage;
